import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ComponentAccessor } from './../component-accessor';
import { DynamicContainerDirective } from './../dynamic-container.directive';
import { ComponentDescriptor } from '../component-descriptor';
import { filter, takeUntil } from 'rxjs/operators';
import { DynamicComponentInjectorService } from '../dynamic-component-injector.service';
import {
    Component,
    OnInit,
    OnDestroy,
    ViewChild,
    Input,
    AfterViewInit,
    forwardRef,
} from '@angular/core';
import { SubscriptionMonitor } from 'idk/widgets/common/subscription-monitor';

@Component({
    selector: 'app-dynamic-container',
    templateUrl: './dynamic-container.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DynamicContainerComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class DynamicContainerComponent
    implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor
{
    private _monitor: SubscriptionMonitor = new SubscriptionMonitor();

    @Input()
    public standalone: boolean = true;

    @Input()
    public descriptor: ComponentDescriptor<any, any>;

    @Input()
    public container: string = null;

    @ViewChild(DynamicContainerDirective)
    private _dynamicContainerDirective: DynamicContainerDirective;

    private _component: ComponentAccessor<any> | ControlValueAccessor;

    public get component(): ComponentAccessor<any> {
        return this._component as ComponentAccessor<any>;
    }

    private _disabled: boolean = false;

    private _value: any;

    private _onChange: (_: any) => void;

    private _onTouched: () => void;

    public constructor(
        private _dynamicComponentInjector: DynamicComponentInjectorService
    ) {}

    public writeValue(obj: any): void {
        this._value = obj;
        setTimeout(() => {
            const component = this._component as ControlValueAccessor;
            if (component?.writeValue) {
                component.writeValue(obj);
            }
        });
    }

    public registerOnChange(fn: (_: any) => void): void {
        this._onChange = fn;
        setTimeout(() => {
            const component = this._component as ControlValueAccessor;
            if (component?.registerOnChange) {
                component.registerOnChange(fn);
            }
        });
    }

    public registerOnTouched(fn: () => void): void {
        this._onTouched = fn;
        setTimeout(() => {
            const component = this._component as ControlValueAccessor;
            if (component?.registerOnTouched) {
                component.registerOnTouched(fn);
            }
        });
    }

    public setDisabledState?(isDisabled: boolean): void {
        this._disabled = isDisabled;
        setTimeout(() => {
            const component = this._component as ControlValueAccessor;
            if (component?.setDisabledState) {
                component.setDisabledState(isDisabled);
            }
        });
    }

    public ngOnInit(): void {
        if (!this.standalone) {
            this._dynamicComponentInjector.injector
                .pipe(
                    takeUntil(this._monitor.subject$),
                    filter(
                        (component: ComponentDescriptor<any, any>) =>
                            component?.container == this.container
                    )
                )
                .subscribe({
                    next: (component: ComponentDescriptor<any, any>) => {
                        setTimeout(() => this._loadComponent(component));
                    },
                });
        }
    }

    public ngAfterViewInit(): void {
        if (this.standalone) {
            setTimeout(() => {
                this._loadComponent(this.descriptor);
            });
        }
    }

    private _loadComponent(descriptor: ComponentDescriptor<any, any>) {
        const viewContainerRef =
            this._dynamicContainerDirective.viewContainerRef;

        viewContainerRef.clear();

        this._component = null;

        if (descriptor?.component == null) {
            return;
        }

        const componentRef = viewContainerRef.createComponent<
            ComponentAccessor<any>
        >(descriptor.component);

        this._component = componentRef.instance;

        this._component.context = descriptor.context;

        setTimeout(() => {
            const component = this._component as ControlValueAccessor;
            if (component?.registerOnChange) {
                component.registerOnChange(this._onChange);
            }

            if (component?.registerOnTouched) {
                component.registerOnTouched(this._onTouched);
            }

            if (component?.setDisabledState) {
                component.setDisabledState(this._disabled);
            }

            if (component?.writeValue) {
                component?.writeValue(this._value);
            }
        });
    }

    public ngOnDestroy(): void {
        this._monitor.unsubscribe();
    }
}
